import React, { useEffect, useState } from 'react';
import { Download, X, Smartphone } from 'lucide-react';

/**
 * Cross-platform PWA install prompt.
 *
 * - On Chrome / Edge / Android: listens for the native `beforeinstallprompt`
 *   event and shows a custom install card. Calling `prompt()` opens the
 *   browser sheet.
 * - On iOS Safari (the most awkward case): the `beforeinstallprompt` event
 *   never fires. We detect iOS + non-standalone display, and show our own
 *   step-by-step instructions card pointing at the Share → Add to Home Screen
 *   flow.
 *
 * Dismissals are remembered in localStorage so we don't nag the user. A
 * 14-day cooldown lets us re-prompt later when they've grown attached.
 */
const DISMISS_KEY = 'nia_install_dismissed_at';
const COOLDOWN_DAYS = 14;

function isIos() {
    if (typeof window === 'undefined') return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

function recentlyDismissed() {
    try {
        const at = localStorage.getItem(DISMISS_KEY);
        if (!at) return false;
        const days = (Date.now() - parseInt(at, 10)) / (1000 * 60 * 60 * 24);
        return days < COOLDOWN_DAYS;
    } catch (e) { return false; }
}

export default function InstallPrompt() {
    const [deferred, setDeferred] = useState(null);
    const [showIos, setShowIos] = useState(false);
    const [showAndroid, setShowAndroid] = useState(false);

    useEffect(() => {
        if (isStandalone()) return;
        if (recentlyDismissed()) return;
        if (isIos()) {
            // Delay so the prompt feels intentional rather than ambushy.
            const t = setTimeout(() => setShowIos(true), 4500);
            return () => clearTimeout(t);
        }
        const handler = (e) => {
            e.preventDefault();
            setDeferred(e);
            setShowAndroid(true);
        };
        window.addEventListener('beforeinstallprompt', handler);
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (e) { console.debug('InstallPrompt: localStorage write failed (private mode); dismissal will not persist', e); }
        setShowIos(false);
        setShowAndroid(false);
    };

    const installNative = async () => {
        if (!deferred) return;
        deferred.prompt();
        await deferred.userChoice;
        setDeferred(null);
        setShowAndroid(false);
    };

    if (showAndroid) {
        return (
            <div className="nia-install-prompt" role="dialog" data-testid="install-prompt-android">
                <button className="nia-install-x" aria-label="Dismiss" onClick={dismiss} data-testid="install-dismiss"><X size={18} /></button>
                <div className="nia-install-row">
                    <div className="nia-install-icon"><Smartphone size={20} /></div>
                    <div>
                        <div className="nia-install-title">Install Nia</div>
                        <div className="nia-install-body">Get one-tap access from your home screen. Works offline too.</div>
                    </div>
                </div>
                <button className="nia-install-cta" onClick={installNative} data-testid="install-confirm"><Download size={14} /> Install</button>
            </div>
        );
    }

    if (showIos) {
        return (
            <div className="nia-install-prompt" role="dialog" data-testid="install-prompt-ios">
                <button className="nia-install-x" aria-label="Dismiss" onClick={dismiss} data-testid="install-dismiss"><X size={18} /></button>
                <div className="nia-install-row">
                    <div className="nia-install-icon"><Smartphone size={20} /></div>
                    <div>
                        <div className="nia-install-title">Add Nia to Home Screen</div>
                        <div className="nia-install-body">Tap the <b>Share</b> button below → then <b>Add to Home Screen</b>. Nia will launch full-screen, just like a native app.</div>
                    </div>
                </div>
            </div>
        );
    }
    return null;
}
